import { ApiInstance } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Divider, Icon } from '@advanza/ui'
import Button from '@advanza/ui/Button'
import clsx from 'clsx'
import Card from 'components/Card'
import FranchisePresenceSubscriptionsManager from 'components/franchisePresenceSubscriptions/FranchisePresenceSubscriptionsManager'
import AutoRow from 'components/misc/AutoRow'
import ExportFeedbackButton from 'components/misc/ExportFeedbackButton'
import ExportLinkExchangeWidget from 'components/misc/ExportLinkExchangeWidget'
import ExportPartialMatchesButton from 'components/misc/ExportPartialMatchesButton'
import ExportProvidersByLicence from 'components/misc/ExportProvidersByLicence'
import ExportProvidersByRegion from 'components/misc/ExportProvidersByRegion'
import ExportPWAStatsButton from 'components/misc/ExportPWAStatsButton'
import GiveAwayFreeLeads from 'components/misc/GiveAwayFreeLeads.tsx'
import ImportPlatformUrls from 'components/misc/ImportPlatformUrls'
import PocWhatsappAnalyticsMisc from 'components/misc/PocWhatsappAnalyticsMisc'
import PreviewDemandQuickReplies from 'components/misc/PreviewDemandQuickReplies'
import ProviderPresenceSubscriptionsMisc from 'components/misc/ProviderPresenceSubscriptionsMisc'
import ResendFailedEmails from 'components/misc/ResendFailedEmails'
import SendMonthlyMails from 'components/misc/SendMonthlyMails'
import { SSO_BRAND, SSO_ICON } from 'components/misc/ssoIcons'
import TestMailTemplate from 'components/misc/TestMailTemplate'
import UploadUserTaggingRules from 'components/misc/UploadUserTaggingRules'
import InvalidateNextPageButton from 'components/services/InvalidateNextPageButton'
import Page from 'components/ui/Page'
import React from 'react'
import { Link } from 'react-router-dom'

// Office buttons send the user to the public-site sibling host so an admin office session
// can't accidentally link an admin account — cookies don't cross subdomains. Backend also
// refuses admin/superuser linking.
const SSO_HOST = window.location.port === '' ? 'https://test.trustoo.nl' : 'http://l.trustoo.nl'

const SsoButton = ({ provider, label, params }) => (
    <a
        href={`${SSO_HOST}/api/auth/sso/${provider}?${params}`}
        className={clsx(
            SSO_BRAND[provider],
            'focus:ring-4 focus:outline-none no-underline shadow-xs border border-transparent font-medium leading-5 rounded text-md px-4 py-2.5 text-center inline-flex items-center transition-colors',
        )}>
        {SSO_ICON[provider]}
        {label}
    </a>
)

const MiscPage = () => {
    const [invalidatePath, setInvalidatePath] = React.useState('')
    const [invalidateTag, setInvalidateTag] = React.useState('')
    return (
        <Page>
            <div className="m-auto max-w-screen-xl my-16 space-y-8">
                <div className="flex space-x-4 items-center">
                    <h1>Misc</h1>
                    <img
                        src="https://static.trustoo.nl/team/haring.png"
                        alt="haring"
                        className="animate-bounce h-16 hover:animate-spin"
                    />
                </div>
                <div className="flex flex-col gap-4">
                    <Card header="Invalidate Static Next Page or Tag">
                        <AutoRow>
                            <InputField
                                placeholder="path e.g.: /bedrijf-aanmelden/"
                                onChange={(e) => setInvalidatePath(e.target.value)}
                                value={invalidatePath}
                            />

                            <InputField
                                placeholder="Or Tags e.g.: services|all|schilder"
                                onChange={(e) => setInvalidateTag(e.target.value)}
                                before={<Icon name="humerus" />}
                                value={invalidateTag}
                            />
                            <InvalidateNextPageButton
                                paths={[invalidatePath]}
                                tag={invalidateTag}
                            />
                        </AutoRow>
                    </Card>
                    <Card header="Monthly mails premium">
                        <SendMonthlyMails />
                    </Card>
                    <Card header="Test Email Templates">
                        <TestMailTemplate />
                    </Card>
                    <Card header="Give Away Free Leads">
                        <GiveAwayFreeLeads />
                    </Card>
                    <Card useToggle header="Export">
                        <ExportProvidersByRegion />
                        <Divider m />
                        <ExportProvidersByLicence />
                        <Divider m />
                        <ExportLinkExchangeWidget />
                        <Divider m />
                        <ExportFeedbackButton />
                    </Card>
                    <Card useToggle header="Import">
                        <ImportPlatformUrls />
                        <Divider m />
                    </Card>
                    <Card
                        header="Resend failed emails"
                        topRight={
                            <small>
                                check issues on{' '}
                                <a
                                    style={{ textDecoration: 'underline' }}
                                    href="https://www.sanity-status.com/">
                                    sanity-status.com
                                </a>
                            </small>
                        }>
                        <ResendFailedEmails />
                    </Card>
                    <Card header="Auto-quotes reports">
                        <Link
                            to={`${ApiInstance.getAddress()}office/auto-quotes-reports/auto-quotes-sent`}
                            target="_blank">
                            <Button>Auto-quotes sent</Button>
                        </Link>
                        <Divider s />
                        <Link
                            to={`${ApiInstance.getAddress()}office/auto-quotes-reports/activations`}
                            target="_blank">
                            <Button>Activations</Button>
                        </Link>
                    </Card>
                    <Card header="Whatsapp to demand report">
                        <Link
                            to={`${ApiInstance.getAddress()}office/whatsapp-to-demand-report/get-report`}
                            target="_blank">
                            <Button>Whatsapp to demand</Button>
                        </Link>
                    </Card>
                    <Card useToggle header="POC WhatsApp analytics">
                        <PocWhatsappAnalyticsMisc />
                    </Card>
                    <Card header="Filters added manually report">
                        <Link
                            to={`${ApiInstance.getAddress()}office/filters-added-manually-report/get-report`}
                            target="_blank">
                            <Button>Filters added manually</Button>
                        </Link>
                    </Card>
                    <Card header="PWA Logs">
                        <ExportPWAStatsButton />
                    </Card>
                    <Card header="Provider Presence Subscriptions">
                        <ProviderPresenceSubscriptionsMisc />
                    </Card>
                    <Card header="Franchise Presence Subscriptions">
                        <FranchisePresenceSubscriptionsManager />
                    </Card>
                    <Card header="Upload user tagging rules">
                        <UploadUserTaggingRules />
                    </Card>
                    <Card header="Test Demand AI Quick Replies">
                        <PreviewDemandQuickReplies />
                    </Card>
                    <Card header="Redirects">
                        <Link to="/misc/redirects">
                            <Button>Manage Redirects</Button>
                        </Link>
                    </Card>
                    <Card header="WhatsApp Business integration">
                        <Link to="/misc/waba-test-throw-away">
                            <Button>Open WhatsApp tools</Button>
                        </Link>
                    </Card>
                    <Card header="Partial matches">
                        <ExportPartialMatchesButton />
                    </Card>
                    <Card header="WorkOS SSO (TRUS-8014)">
                        <p className="text-sm text-neutral-500 mb-4">
                            Buttons jump to <code>{SSO_HOST}</code>. Sessions there are independent
                            of the office, so admin linking can't happen by accident.
                        </p>

                        <h4 className="text-md font-semibold mb-1">Sign in (strict)</h4>
                        <p className="text-sm text-neutral-500 mb-2">
                            Logs in if the Google/Apple identity is already attached, or if the
                            email matches an existing non-admin account. Refuses with{' '}
                            <code>?error=sso_no_account</code> for unknown emails.
                        </p>
                        <SsoButton
                            provider="google"
                            label="Sign in with Google"
                            params="intent=login&redirect=/"
                        />
                        <Divider s />
                        <SsoButton
                            provider="apple"
                            label="Sign in with Apple"
                            params="intent=login&redirect=/"
                        />

                        <Divider m />
                        <h4 className="text-md font-semibold mb-1">Continue (login or sign up)</h4>
                        <p className="text-sm text-neutral-500 mb-2">
                            Same as Sign in, but unknown emails get a fresh <code>demand</code>{' '}
                            account auto-created (country defaults to NL).
                        </p>
                        <SsoButton
                            provider="google"
                            label="Continue with Google"
                            params="intent=login&allow_signup=1&redirect=/&country=NL"
                        />
                        <Divider s />
                        <SsoButton
                            provider="apple"
                            label="Continue with Apple"
                            params="intent=login&allow_signup=1&redirect=/&country=NL"
                        />

                        {window._user && window._user.id && (
                            <>
                                <Divider m />
                                <h4 className="text-md font-semibold mb-1">
                                    Connect (link to current account)
                                </h4>
                                <p className="text-sm text-neutral-500 mb-2">
                                    Attaches the Google/Apple identity to whichever account is
                                    logged in on <code>{SSO_HOST}</code>. Refused for admin accounts
                                    (out of scope for this ticket).
                                </p>
                                <SsoButton
                                    provider="google"
                                    label="Connect Google"
                                    params="intent=link&redirect=/"
                                />
                                <Divider s />
                                <SsoButton
                                    provider="apple"
                                    label="Connect Apple"
                                    params="intent=link&redirect=/"
                                />
                            </>
                        )}
                    </Card>
                </div>
            </div>
        </Page>
    )
}

export default MiscPage
